<template>
  <div ref="toolTipContainerRef" class="tooltip" role="tooltip">
    <Transition name="fade">
      <div
        v-show="isToolTipOpen"
        :id="tooltipId"
        ref="toolTipRef"
        class="tool-tip-floating bg-white p-space-xxs rounded-sm fs-xs"
        :class="{ shadow: displayShadow }"
        :style="[floatingStyles, setTooltipWidth]"
        role="tooltip"
        :aria-hidden="!isToolTipOpen"
        aria-live="assertive"
        tabindex="0"
      >
        <slot name="tooltip"></slot>
      </div>
    </Transition>

    <button
      ref="reference"
      class="tool-tip-icon ms-space-xxs"
      :aria-label="ariaLabel"
      :aria-describedby="tooltipId"
      :aria-expanded="isToolTipOpen"
      @focus="showToolTip"
      @click="showToolTip"
      @mouseenter="showToolTip"
      @mouseleave="hideToolTip"
      @blur="hideToolTip"
      @focusout="handleFocusOut"
    >
      <slot></slot>
    </button>
  </div>
</template>
<script lang="ts" setup>
import {
  autoUpdate,
  flip,
  offset as offsetMiddleware,
  shift,
  size,
  useFloating,
  type OffsetOptions,
  type Placement
} from "@floating-ui/vue";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

interface Props {
  placement?: Placement;
  offset?: OffsetOptions;
  transform?: boolean;
  tooltipWidth?: number;
  displayShadow?: boolean;
  tooltipId?: string;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placement: "right-start",
  offset: 0,
  tooltipWidth: 210,
  displayShadow: true,
  ariaLabel: "Toggle tooltip"
});

const isToolTipOpen = ref(false);
const toolTipRef = ref<HTMLElement | null>(null);
const reference = ref<HTMLElement | null>(null);
const toolTipContainerRef = ref<HTMLElement | null>(null);
const initialScrollY = ref<number>(0);
const { floatingStyles } = useFloating(reference, toolTipRef, {
  whileElementsMounted: autoUpdate,
  strategy: "absolute",
  open: isToolTipOpen,
  transform: props.transform,
  placement: props.placement,
  // middleware: [flip(), offset(props.offset), shift()],
  middleware: [
    flip(),
    offsetMiddleware(props.offset),
    shift({ padding: 8 }),
    size({
      padding: 8,
      apply({ availableWidth, availableHeight, elements }) {
        Object.assign(elements.floating.style, {
          maxWidth: `${availableWidth}px`,
          maxHeight: `${availableHeight}px`
        });
      }
    })
  ]
});

const getScrollY = () => window.scrollY || document.documentElement.scrollTop || 0;

const handleDocumentClick = (event: MouseEvent) => {
  const target = event.target;
  if (target instanceof Node && toolTipContainerRef.value?.contains(target)) {
    return;
  }
  hideToolTip();
};

const handleWindowScroll = () => {
  if (isToolTipOpen.value && Math.abs(getScrollY() - initialScrollY.value) > 50) {
    hideToolTip();
  }
};

const outsideClickListenerOptions = { capture: true, passive: true };

const showToolTip = () => {
  console.log("show tooltip");
  initialScrollY.value = getScrollY();
  isToolTipOpen.value = true;
};

const hideToolTip = () => {
  isToolTipOpen.value = false;
};

const handleFocusOut = (e: FocusEvent) => {
  const relatedTarget = e.relatedTarget as Node | null;
  if (toolTipRef.value?.contains(relatedTarget)) {
    hideToolTip();
  }
};

const setTooltipWidth = computed(() => {
  return { "--tt-width": `${props.tooltipWidth}px` };
});

onMounted(() => {
  document.addEventListener("click", handleDocumentClick, outsideClickListenerOptions);
  window.addEventListener("scroll", handleWindowScroll, { passive: true });
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick, outsideClickListenerOptions);
  window.removeEventListener("scroll", handleWindowScroll);
});

defineExpose<{
  hideTooltip: () => void;
}>({
  hideTooltip: hideToolTip
});
</script>

<style lang="scss" scoped>
.tool-tip-icon {
  background-color: transparent;
  border: none;
  cursor: pointer;
  appearance: none;
  padding: 0;
  margin: 0;
}

.tool-tip-floating {
  position: absolute;
  z-index: 1100;
  padding: 0.5rem;
  max-width: 210px;
  white-space: normal;
  word-break: break-word;
  word-wrap: break-word;
  overflow-y: auto;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
@media screen and (min-width: 992px) {
  .tool-tip-floating {
    max-width: var(--tt-width);
    width: var(--tt-width);
  }
}
</style>
